/**
 * finance-core — FIRE (Financial Independence, Retire Early)
 *
 * Rule of 25: cần tích lũy 25× chi tiêu hàng năm.
 * 4% Rule: rút 4%/năm từ danh mục mà không cạn kiệt.
 * Mọi phép tính "bao lâu nữa đạt FIRE" đều PHẢI tính số dư hiện có làm gốc
 * — dùng monthsToReachGoal, không tự chế công thức niên kim thuần.
 */

import { futureValue, monthsToReachGoal } from './compound';

export interface FIREMetrics {
  fireNumber: number;
  gap: number;
  yearsToFIRE: number;
  monthlyWithdrawal: number;
  achieved: boolean;
  message: string;
}

export const fireNumber = (annualExpenses: number, multiplier = 25): number =>
  annualExpenses * multiplier;

// Giả định lợi nhuận CHUNG toàn app: 7%/năm (quyết định 24/07/2026 —
// phía thận trọng của hoạch định). Mọi nơi hiển thị years-to-FIRE phải
// dùng cùng giả định này, trừ khi người dùng chỉnh (tương lai).
export const DEFAULT_INVESTMENT_RETURN_PERCENT = 7;

export const calculateFIREMetrics = (
  annualExpenses: number,
  currentNetWorth: number,
  monthlySavings: number,
  investmentReturnPercent = DEFAULT_INVESTMENT_RETURN_PERCENT,
  multiplier = 25
): FIREMetrics => {
  const target = fireNumber(annualExpenses, multiplier);
  const gap = target - currentNetWorth;

  if (gap <= 0) {
    return {
      fireNumber: target,
      gap: 0,
      yearsToFIRE: 0,
      monthlyWithdrawal: annualExpenses / 12,
      achieved: true,
      message: 'Chúc mừng! Bạn đã đạt FIRE - Có thể nghỉ hưu sớm ngay!'
    };
  }

  const months = monthsToReachGoal(
    currentNetWorth,
    monthlySavings,
    investmentReturnPercent,
    target
  );

  return {
    fireNumber: target,
    gap,
    yearsToFIRE: months / 12,
    monthlyWithdrawal: annualExpenses / 12,
    achieved: false,
    message: months === Infinity
      ? (monthlySavings < 0
          ? 'Chi đang vượt thu — cân bằng thu chi trước khi dự tính thời điểm FIRE'
          : 'Chưa có tiết kiệm hàng tháng - Không thể dự tính thời điểm FIRE')
      : `Cần tích lũy thêm ${(gap / 1_000_000).toFixed(0)}M trong ${(months / 12).toFixed(1)} năm để FIRE`
  };
};

export interface RetirementReadinessInput {
  age: number;
  targetRetirementAge?: number;
  currentNetWorth: number;
  annualExpenses: number;
  monthlySavings: number;
  expectedReturnPercent?: number;
}

export interface RetirementReadiness {
  yearsToRetirement: number;
  fireNumber: number;
  projectedNetWorth: number;
  readinessPercent: number;
  shortfall: number;
  status: 'excellent' | 'good' | 'warning' | 'critical';
  message: string;
}

export const calculateRetirementReadiness = ({
  age,
  targetRetirementAge = 60,
  currentNetWorth,
  annualExpenses,
  monthlySavings,
  expectedReturnPercent = DEFAULT_INVESTMENT_RETURN_PERCENT
}: RetirementReadinessInput): RetirementReadiness => {
  const yearsToRetirement = targetRetirementAge - age;
  const target = fireNumber(annualExpenses);

  const projectedNetWorth = futureValue(
    currentNetWorth,
    monthlySavings,
    expectedReturnPercent,
    yearsToRetirement
  );

  const readinessPercent = target > 0 ? (projectedNetWorth / target) * 100 : 0;

  let status: RetirementReadiness['status'] = 'critical';
  let message = 'Cần điều chỉnh kế hoạch nghiêm túc';

  if (readinessPercent >= 100) {
    status = 'excellent';
    message = 'Trên đà nghỉ hưu thoải mái!';
  } else if (readinessPercent >= 75) {
    status = 'good';
    message = 'Đang tiến triển tốt';
  } else if (readinessPercent >= 50) {
    status = 'warning';
    message = 'Cần tăng tỷ lệ tiết kiệm';
  }

  return {
    yearsToRetirement,
    fireNumber: target,
    projectedNetWorth,
    readinessPercent,
    shortfall: Math.max(0, target - projectedNetWorth),
    status,
    message
  };
};
